import { Injectable, NgZone } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'enydea-app-theme';
const DARK_CLASS = 'dark-theme';
/** Ionic's dark variables in theme/variables.scss hang off this class. */
const IONIC_DARK_CLASS = 'app-dark';

/**
 * Light / dark mode for the native app, chosen in More -> Appearance and kept on the
 * device (the web app keeps using the account's `darktheme`). One switch drives both
 * styling systems the app mixes: Angular Material (body.dark-theme + overlay container)
 * and Ionic (html.app-dark), plus the status bar icon colour.
 */
@Injectable({ providedIn: 'root' })
export class AppThemeService {
  private media = window.matchMedia('(prefers-color-scheme: dark)');
  private started = false;

  constructor(private overlay: OverlayContainer, private zone: NgZone) {}

  get mode(): ThemeMode {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'light' || saved === 'dark' ? saved : 'system';
    } catch {
      return 'system';
    }
  }

  /** Applies the saved mode and follows the phone's setting while the mode is 'system'. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.media.addEventListener('change', () => this.zone.run(() => {
      if (this.mode === 'system') this.apply();
    }));
    this.apply();
  }

  setMode(mode: ThemeMode): void {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Private storage unavailable: the choice still applies for this session.
    }
    this.apply(mode);
  }

  private apply(mode: ThemeMode = this.mode): void {
    const dark = mode === 'dark' || (mode === 'system' && this.media.matches);
    document.documentElement.classList.toggle(IONIC_DARK_CLASS, dark);
    document.documentElement.classList.toggle('ion-palette-dark', dark);
    document.body.classList.toggle(DARK_CLASS, dark);
    this.overlay.getContainerElement().classList.toggle(DARK_CLASS, dark);
    if (Capacitor.isNativePlatform()) {
      // Style.Dark = light icons for a dark background, and the other way round.
      void StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => undefined);
    }
  }
}
